import React from 'react';
import { Calendar } from 'lucide-react';
import { useDateRange } from '../contexts/DateRangeContext';
import { getDateRange } from '../utils/dataProcessor';

const DateRangePicker: React.FC = () => {
  const { dateRange, setDateRange, resetToDefault } = useDateRange();
  const fullRange = getDateRange();

  const handleStartDateChange = (date: string) => {
    setDateRange({ ...dateRange, startDate: date });
  };

  const handleEndDateChange = (date: string) => {
    setDateRange({ ...dateRange, endDate: date });
  };

  const applyQuickRange = (days: number) => {
    const endDate = new Date(fullRange.endDate);
    const startDate = new Date(fullRange.endDate);
    startDate.setDate(endDate.getDate() - days + 1);
    
    setDateRange({
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    });
  };

  const applyThisMonth = () => {
    const today = new Date(fullRange.endDate);
    // Ensure we get the correct first day of the current month
    const year = today.getFullYear();
    const month = today.getMonth();
    const firstDay = new Date(year, month, 1);
    
    setDateRange({
      startDate: `${year}-${String(month + 1).padStart(2, '0')}-01`,
      endDate: today.toISOString().split('T')[0]
    });
  };

  const applyLastMonth = () => {
    const today = new Date(fullRange.endDate);
    const year = today.getFullYear();
    const month = today.getMonth(); // Current month (0-11)
    
    // Calculate last month's year and month
    const lastMonthYear = month === 0 ? year - 1 : year;
    const lastMonth = month === 0 ? 11 : month - 1;
    
    // First day of last month
    const firstDay = new Date(lastMonthYear, lastMonth, 1);
    // Last day of last month (day 0 of current month = last day of previous month)
    const lastDay = new Date(year, month, 0);
    
    setDateRange({
      startDate: `${lastMonthYear}-${String(lastMonth + 1).padStart(2, '0')}-01`,
      endDate: `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-2">
          <Calendar className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">日付範囲</h3>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
          {/* Quick selection buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => applyQuickRange(7)}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              過去7日間
            </button>
            <button
              onClick={() => applyQuickRange(30)}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              過去30日間
            </button>
            <button
              onClick={() => applyQuickRange(90)}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              過去3ヶ月
            </button>
            <button
              onClick={applyThisMonth}
              className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
            >
              今月
            </button>
            <button
              onClick={applyLastMonth}
              className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
            >
              先月
            </button>
            <button
              onClick={resetToDefault}
              className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
            >
              すべて
            </button>
          </div>

          {/* Date input fields */}
          <div className="flex items-center space-x-2">
            <input
              type="date"
              value={dateRange.startDate}
              min={fullRange.startDate}
              max={fullRange.endDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <span className="text-gray-500">~</span>
            <input
              type="date"
              value={dateRange.endDate}
              min={fullRange.startDate}
              max={fullRange.endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateRangePicker; 